import { MicOff, Square } from 'lucide-react'
import type { CaptureLossReason } from '@/stores/recording'
import { captureLostCopy } from '@/lib/capture-lost-copy'
import { cn } from '@/utils/cn'

/**
 * The microphone stopped mid-consult (CLA-1341). Loud on purpose: the failure
 * this replaces was a timer that kept climbing over a dead mic, so the
 * clinician found out from an empty note an hour later. The number shown is
 * the frozen capture length — what will actually be transcribed.
 *
 * A `muted` track (iPhone screen lock, app switch) comes back on its own once
 * the phone is unlocked, and recording carries on; `ended` and `error` do not,
 * and finishing keeps everything up to the cut.
 *
 * With `onBackup` the main recorder has stopped but the segment roller is
 * still capturing (CLA-1552): recording carries on, and finishing keeps it.
 */
export function CaptureLostNotice({
  reason,
  elapsed,
  onFinish,
  onBackup = false,
  compact = false,
  className,
}: {
  reason: CaptureLossReason
  elapsed: number
  onFinish: () => void
  /** The segment roller is still recording (CLA-1552): say so. */
  onBackup?: boolean
  /** Mobile pill: tighter and radius-matched. */
  compact?: boolean
  className?: string
}) {
  const copy = captureLostCopy(reason, elapsed, onBackup)
  return (
    <div
      role="alert"
      data-testid="capture-lost"
      className={cn(
        'flex flex-col gap-2 bg-warning/15 border border-warning/40 shadow-lg',
        compact ? 'rounded-2xl px-3 py-2.5 max-w-[calc(100vw-2rem)]' : 'rounded-lg px-4 py-3',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <MicOff className="w-4 h-4 text-warning shrink-0 mt-0.5" />
        <div className="flex flex-col gap-0.5">
          <span className={cn('font-semibold text-on-warning-soft', compact ? 'text-xs' : 'text-sm')}>
            {copy.title}
          </span>
          <span className={cn('text-ink-secondary', compact ? 'text-[11px]' : 'text-xs')}>
            {copy.detail}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onFinish}
        className={cn(
          'self-start inline-flex items-center gap-1.5 rounded-lg bg-warning text-white font-medium hover:bg-warning/90 transition-colors',
          compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs',
        )}
      >
        <Square className="w-3 h-3" />
        {copy.finish}
      </button>
    </div>
  )
}
